import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Joyride, { CallBackProps, Step } from 'react-joyride';
import { TF } from 'react-translator-component';
import { useTheme } from 'styled-components';

import { moveFor, targetReady } from './tourFlow';
import { TOUR_STEPS, TourChapter } from './tourSteps';
import { markTourSeen } from './tourStorage';

import { COLORS } from '../../../../utils/theme/colors';
import { InteroperabilityTabKey } from '../tabs';

/**
 * O tutorial de primeiro uso do módulo (MV2-1791).
 *
 * ## Por que react-joyride, e por que a 2.5.5
 *
 * O `Tour` do Ant Design só existe no antd v5 e aqui o antd é `~4.10.0`, então ele estava fora
 * sem subir o antd — o que não é escopo desta subtarefa.
 *
 * Das quatro bibliotecas levantadas, a react-joyride é a mais completa (passos controlados,
 * spotlight, rolagem até o alvo, callbacks por passo) e a única que compila neste projeto. A
 * versão está **presa em 2.5.5, exata, de propósito**: da 2.7 em diante o campo `module` do
 * pacote passou a apontar para um `.mjs`, e o webpack 4 do CRA 4 trata `.mjs` como módulo ESM
 * estrito — importar `Component` do React (que é CommonJS) ali dentro falha o build com
 * "Can't import the named export 'Component' from non EcmaScript module". Na 3.2.0 é pior: as
 * dependências novas usam optional chaining, que o parser do webpack 4 não lê
 * ("Module parse failed: Unexpected token"). A 2.5.5 publica `es/index.js`, com extensão `.js`,
 * e por isso passa. As três alternativas (@reactour/tour, driver.js, intro.js-react) publicam
 * `.mjs` do mesmo jeito — e a intro.js ainda é AGPL-3.0, com licença paga para uso proprietário.
 *
 * ## Passo que troca de aba
 *
 * O alvo de cada aba só existe quando ela está ativa, e o balão não pode ser posicionado antes
 * disso. Daí o `pending`: ao avançar, o tutorial **para** (`run` falso), troca a aba, espera o
 * alvo montar com caixa maior que zero e só então retoma no passo novo. Sem essa parada o balão
 * aparecia por um quadro no canto da tela, ancorado no alvo da aba anterior — que o antd mantém
 * montado e escondido.
 */

export type InteroperabilityTourProps = {
  /** Se o tutorial deve estar em execução. O shell decide (primeiro acesso ou botão). */
  open: boolean;
  /**
   * Capítulo a rodar. Sem valor é o tutorial completo, que é o do primeiro acesso.
   *
   * Só o completo marca "já viu": quem pediu para ver apenas o fluxo de convite não disse que
   * conhece o módulo, e ainda merece o tutorial inteiro no primeiro acesso.
   */
  chapter?: TourChapter;
  activeTab: InteroperabilityTabKey;
  onSelectTab: (tab: InteroperabilityTabKey) => void;
  /** Concluído, pulado ou fechado no X. */
  onClose: () => void;
};

/**
 * Teto da espera pelo alvo: ~1s a 60fps.
 *
 * Existe para o tutorial não ficar preso se a aba não montar o alvo (erro de carga na aba, alvo
 * removido num refactor). Estourado o teto, o passo é mostrado de qualquer forma e o próprio
 * Joyride emite `TARGET_NOT_FOUND`, que o `moveFor` trata avançando.
 */
const MAX_WAIT_FRAMES = 60;

/**
 * Espera curta para o passo de alvo opcional (~100ms).
 *
 * Alvo opcional que não está montado não vai montar: o banner de convites não existe sem
 * convite pendente. Esperar o mesmo segundo dos obrigatórios só atrasaria o pulo.
 */
const OPTIONAL_WAIT_FRAMES = 6;

export const InteroperabilityTour: React.FC<InteroperabilityTourProps> = ({
  open,
  chapter,
  activeTab,
  onSelectTab,
  onClose,
}) => {
  // O ThemeProvider do dashboard recebe uma função; o styled-components resolve e entrega o
  // objeto do tenant aqui (`{ primary, ... }` de utils/theme/tenantColors).
  const theme = useTheme() as { primary?: string } | undefined;

  /**
   * O roteiro é remontado a cada abertura, e não uma vez na montagem: `TOUR_STEPS()` chama `T()`
   * e lê a permissão de moderar, então congelá-lo deixaria o texto no idioma de quando a página
   * carregou.
   */
  const script = useMemo(() => (open ? TOUR_STEPS(chapter) : []), [chapter, open]);

  const [index, setIndex] = useState(0);

  /** Passo pedido, ainda esperando a aba trocar e o alvo montar. */
  const [pending, setPending] = useState<number | null>(null);

  /** Aba em que a pessoa estava quando o tutorial abriu, para devolvê-la no fim. */
  const startTab = useRef<InteroperabilityTabKey>(activeTab);

  useEffect(() => {
    if (!open) {
      return;
    }

    startTab.current = activeTab;
    setIndex(0);
    setPending(0);
    // `activeTab` fica fora das dependências de propósito: reagir a ele aqui reiniciaria o
    // tutorial a cada troca de aba — inclusive nas trocas que o próprio tutorial provoca.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const finish = useCallback(() => {
    // Só o tutorial completo marca a conta como "já viu" (ver o comentário da prop `chapter`).
    if (!chapter) {
      markTourSeen();
    }

    setPending(null);

    /*
      Voltar o índice para zero aqui, e não só ao reabrir.

      O efeito de abertura só roda **depois** do primeiro render com `open` verdadeiro, e nesse
      render o Joyride já monta com o `stepIndex` que sobrou da vez anterior. Se o tutorial
      tinha parado no passo do Histórico, reabrir mostraria aquele passo por um quadro — ou
      pior, o alvo não estaria na tela, o Joyride emitiria `TARGET_NOT_FOUND` e o `moveFor`
      avançaria o roteiro antes de a abertura zerar. Zerado no fim, o render de reabertura cai
      sempre no passo do cabeçalho, cujo alvo existe em qualquer aba.
    */
    setIndex(0);

    if (startTab.current !== activeTab) {
      onSelectTab(startTab.current);
    }

    onClose();
  }, [activeTab, chapter, onClose, onSelectTab]);

  useEffect(() => {
    if (!open || pending === null) {
      return undefined;
    }

    const step = script[pending];

    // Primeiro a aba. A troca muda `activeTab`, este efeito roda de novo e cai na espera abaixo.
    if (step.tab && step.tab !== activeTab) {
      onSelectTab(step.tab);
      return undefined;
    }

    const teto = step.optional ? OPTIONAL_WAIT_FRAMES : MAX_WAIT_FRAMES;

    let frames = 0;
    let frame = 0;

    const wait = () => {
      if (targetReady(document.querySelector(step.target))) {
        setIndex(pending);
        setPending(null);
        return;
      }

      if (frames >= teto) {
        /*
          Alvo opcional que não apareceu: o passo é pulado, e não mostrado ancorado em nada.

          É o caso do banner de convites, que só existe quando há convite pendente — a situação
          comum de quem está vendo o tutorial pela primeira vez. Mostrar o balão ali faria o
          Joyride emitir TARGET_NOT_FOUND e o texto apareceria no canto da tela.
        */
        if (step.optional) {
          const proximo = pending + 1;

          if (proximo >= script.length) {
            finish();
          } else {
            setPending(proximo);
          }

          return;
        }

        // Obrigatório que não montou: mostra de todo jeito. O TARGET_NOT_FOUND do Joyride cai
        // no `moveFor`, que avança — ficar parado prenderia a pessoa num balão sem botão útil.
        setIndex(pending);
        setPending(null);
        return;
      }

      frames += 1;
      frame = requestAnimationFrame(wait);
    };

    frame = requestAnimationFrame(wait);

    return () => cancelAnimationFrame(frame);
  }, [activeTab, finish, onSelectTab, open, pending, script]);

  const handleCallback = useCallback(
    ({ action, index: at, status, type }: CallBackProps) => {
      const move = moveFor({ action, index: at, status, type }, script.length);

      if (move.kind === 'end') {
        finish();
        return;
      }

      if (move.kind === 'go') {
        setPending(move.index);
      }
    },
    [finish, script.length],
  );

  const steps: Step[] = useMemo(
    () =>
      script.map((step) => ({
        target: step.target,
        title: step.title,
        content: step.content,
        // Sem farol: o tutorial abre já com o balão. O farol é para tutorial que espera clique,
        // e aqui a pessoa não sabe que ele existe para clicar.
        disableBeacon: true,
        // `auto` porque os alvos das abas são regiões altas: `bottom` (o padrão) jogaria o balão
        // para fora da tela.
        placement: 'auto' as const,
      })),
    [script],
  );

  if (!open) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={pending === null}
      stepIndex={index}
      callback={handleCallback}
      continuous
      showProgress
      showSkipButton
      // Clique fora não fecha: o tutorial cobre a tela inteira e fechar sem querer no primeiro
      // passo é o mesmo que nunca ter visto o tutorial.
      disableOverlayClose
      scrollToFirstStep
      /*
        `TF` e não `T` nos rótulos dos botões.

        O `T()` passa pelo react-html-parser e devolve um **array** de nós — e o Joyride mescla
        este `locale` com o padrão dele por deepmerge, que **concatena** arrays. Com `T()` o
        botão saía escrito "Skip tourSkip tour", e o aria-label também. `TF` devolve string,
        que é como o resto do módulo já faz quando precisa do texto, e não do nó.
      */
      locale={{
        back: TF('Back'),
        close: TF('Close'),
        last: TF('Got it'),
        // Chave própria, e não a 'Next' que já existe: aquela é da paginação e sai como
        // "Próxima" (página) em pt-BR, que num balão de tutorial fica errado.
        next: TF('Next step'),
        skip: TF('Skip tour'),
      }}
      styles={{
        options: {
          // A cor do tenant, como todo estado marcado do módulo. Sem ela o Joyride sai no
          // rosa padrão dele, que não é a cor de nenhum tenant.
          primaryColor: theme?.primary || COLORS.primary,
          backgroundColor: '#fff',
          arrowColor: '#fff',
          textColor: '#2d3339',
          overlayColor: 'rgba(18, 38, 63, 0.55)',
          // Acima do Modal e do Drawer do antd (1000): o módulo abre painel de detalhe em portal.
          zIndex: 1100,
        },
        tooltipTitle: {
          fontSize: 15,
          fontWeight: 600,
          color: '#12263f',
          textAlign: 'left',
        },
        tooltipContent: {
          fontSize: 13,
          lineHeight: 1.5,
          textAlign: 'left',
        },
      }}
    />
  );
};
